import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "checkbox",
    "selectAll",
    "bulkActions",
    "deleteButton",
    "publishButton",
    "unpublishButton",
    "selectedCount",
    "layoutSelect",
    "selectAllNotice",
    "allSelectedNotice",
  ];
  static values = {
    deleteUrl: String,
    publishUrl: String,
    unpublishUrl: String,
    updateLayoutUrl: String,
    totalCount: Number,
    pageCount: Number,
    search: String,
    status: String,
    pageType: String,
    selectionMode: String, // "page" (default) or "all"
  };

  connect() {
    if (!this.hasSelectionModeValue) {
      this.selectionModeValue = "page";
    }
    this.updateBulkActionsVisibility();
  }

  toggleAll(event) {
    // Manual toggling of checkboxes means we're only selecting on this page
    this.selectionModeValue = "page";

    const checked = event.target.checked;
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = checked;
    });
    this.updateBulkActionsVisibility();
  }

  toggle() {
    // Manual toggling of an individual checkbox resets to page-only selection
    this.selectionModeValue = "page";
    this.updateBulkActionsVisibility();
    this.updateSelectAllState();
  }

  updateSelectAllState() {
    if (!this.hasSelectAllTarget) return;

    const allChecked =
      this.checkboxTargets.length > 0 &&
      this.checkboxTargets.every((cb) => cb.checked);
    const someChecked = this.checkboxTargets.some((cb) => cb.checked);

    this.selectAllTarget.checked = allChecked;
    this.selectAllTarget.indeterminate = someChecked && !allChecked;
  }

  updateBulkActionsVisibility() {
    const selectedIdsCount = this.getSelectedIds().length;
    const totalCount = this.hasTotalCountValue
      ? this.totalCountValue
      : selectedIdsCount;

    const selectedCount =
      this.selectionModeValue === "all" && totalCount > 0
        ? totalCount
        : selectedIdsCount;

    if (this.hasBulkActionsTarget) {
      if (selectedCount > 0) {
        this.bulkActionsTarget.classList.remove("hidden");
      } else {
        this.bulkActionsTarget.classList.add("hidden");
      }
    }

    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = selectedCount;
    }

    // Handle "select all" notices
    const visibleCount = this.hasPageCountValue
      ? this.pageCountValue
      : this.checkboxTargets.length;

    if (this.hasSelectAllNoticeTarget && this.hasAllSelectedNoticeTarget) {
      if (this.selectionModeValue === "all" && selectedCount > 0) {
        // All matching pages are selected
        this.selectAllNoticeTarget.classList.add("hidden");
        this.allSelectedNoticeTarget.classList.remove("hidden");
      } else {
        this.allSelectedNoticeTarget.classList.add("hidden");

        const allVisibleSelected =
          visibleCount > 0 && selectedIdsCount === visibleCount;

        if (allVisibleSelected && totalCount > visibleCount) {
          // Some selected, and there are more matching pages than currently visible
          this.selectAllNoticeTarget.classList.remove("hidden");
        } else {
          this.selectAllNoticeTarget.classList.add("hidden");
        }
      }
    }
  }

  getSelectedIds() {
    return this.checkboxTargets
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => checkbox.value);
  }

  showDeleteModal() {
    // Find the modal element by ID and dispatch the open event
    const modalElement = document.getElementById("bulk-delete-modal");
    if (modalElement) {
      modalElement.dispatchEvent(
        new CustomEvent("modal:open", { bubbles: true }),
      );
    }
  }

  hideDeleteModal() {
    // Find the modal element by ID and dispatch the close event
    const modalElement = document.getElementById("bulk-delete-modal");
    if (modalElement) {
      modalElement.dispatchEvent(
        new CustomEvent("modal:close", { bubbles: true }),
      );
    }
  }

  confirmDelete(event) {
    event.preventDefault();
    const selectedIds = this.getSelectedIds();
    if (selectedIds.length === 0 && this.selectionModeValue !== "all") {
      alert("No pages selected");
      return;
    }

    this.submitBulkAction(this.deleteUrlValue, selectedIds);
  }

  publish(event) {
    event.preventDefault();
    const selectedIds = this.getSelectedIds();

    if (selectedIds.length === 0 && this.selectionModeValue !== "all") {
      alert("No pages selected");
      return;
    }

    this.submitBulkAction(this.publishUrlValue, selectedIds);
  }

  unpublish(event) {
    event.preventDefault();
    const selectedIds = this.getSelectedIds();

    if (selectedIds.length === 0 && this.selectionModeValue !== "all") {
      alert("No pages selected");
      return;
    }

    this.submitBulkAction(this.unpublishUrlValue, selectedIds);
  }

  updateLayout(event) {
    event.preventDefault();
    const selectedIds = this.getSelectedIds();
    const layout = this.layoutSelectTarget.value;

    if (selectedIds.length === 0 && this.selectionModeValue !== "all") {
      alert("No pages selected");
      return;
    }

    if (!layout) {
      alert("Please select a layout");
      return;
    }

    this.submitBulkAction(this.updateLayoutUrlValue, selectedIds, {
      layout_template: layout,
    });
  }

  selectAllMatching(event) {
    if (event) event.preventDefault();

    // Switch to "all matching" selection mode
    this.selectionModeValue = "all";

    // Visually select all checkboxes on the current page
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = true;
    });
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = true;
      this.selectAllTarget.indeterminate = false;
    }

    this.updateBulkActionsVisibility();
  }

  clearSelection(event) {
    if (event) event.preventDefault();

    this.selectionModeValue = "page";

    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = false;
    });
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = false;
      this.selectAllTarget.indeterminate = false;
    }

    this.updateBulkActionsVisibility();
  }

  submitBulkAction(url, pageIds, extraParams = {}) {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = url;

    // Add CSRF token
    const csrfToken = document.querySelector(
      'meta[name="csrf-token"]',
    )?.content;
    if (csrfToken) {
      const csrfInput = document.createElement("input");
      csrfInput.type = "hidden";
      csrfInput.name = "authenticity_token";
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }

    // Add selection mode
    const selectionModeInput = document.createElement("input");
    selectionModeInput.type = "hidden";
    selectionModeInput.name = "selection_mode";
    selectionModeInput.value = this.selectionModeValue || "page";
    form.appendChild(selectionModeInput);

    // Add page IDs (used when selection_mode = "page")
    pageIds.forEach((id) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "page_ids[]";
      input.value = id;
      form.appendChild(input);
    });

    // Add current filter params so the server can apply
    // the same filters when selection_mode = "all"
    const filters = {
      search: this.hasSearchValue ? this.searchValue : "",
      status: this.hasStatusValue ? this.statusValue : "",
      page_type: this.hasPageTypeValue ? this.pageTypeValue : "",
    };

    const params = Object.assign({}, filters, extraParams);

    // Add extra parameters (including filters)
    Object.entries(params).forEach(([key, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }
}
